import React from "react";
import { UserX, PhoneOff, FileX, Clock } from "lucide-react";

export const Problem = ({ metierOverride }) => {
  const defaultProblems = [
    {
      icon: UserX,
      title: "Pas assez de clients",
      description: "80% des artisans n'ont pas assez de travail. Vous attendez que le téléphone sonne au lieu de remplir votre agenda.",
    },
    {
      icon: PhoneOff,
      title: "Appels manqués sur chantier",
      description: "Mains occupées, au volant, avec un client — vous ratez l'appel et le prospect part chez le concurrent.",
    },
    {
      icon: FileX,
      title: "Devis jamais relancés",
      description: "70% des devis ne sont jamais suivis. Des milliers de francs dorment dans votre boîte mail.",
    },
    {
      icon: Clock,
      title: "Factures impayées",
      description: "Vous n'avez pas le temps de relancer. Les impayés s'accumulent et votre trésorerie en souffre.",
    },
  ];

  // Use metier-specific problems if available
  const metierProblems = metierOverride?.metierProblemes;
  const icons = [UserX, PhoneOff, FileX, Clock];
  const problems = metierProblems
    ? metierProblems.map((desc, i) => ({
        icon: icons[i % icons.length],
        title: defaultProblems[i]?.title || "Problème courant",
        description: desc,
      }))
    : defaultProblems;

  return (
    <section id="problem" className="py-14 lg:py-28 bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="section-label text-accent mb-4">Le problème</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            {metierOverride?.title
              ? <>{metierOverride.title}{metierOverride.ville ? ` à ${metierOverride.ville.nom}` : ""} — <span className="text-accent">reconnaissez-vous ça ?</span></>
              : "Reconnaissez-vous ces situations ?"}
          </h2>
          <p className="text-base lg:text-lg text-slate-400 max-w-2xl mx-auto">
            {metierOverride?.ville
              ? `Les problèmes quotidiens des ${metierOverride.title.toLowerCase()}s à ${metierOverride.ville.nom}. Ce ne sont pas vos compétences le problème.`
              : "Ce ne sont pas vos compétences le problème. C'est le système qui ne suit pas."}
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <div
                key={problem.title}
                className="bg-slate-800/50 border border-slate-700 rounded-md p-6 hover:border-accent/50 transition-all duration-300"
                data-testid={`problem-card-${index}`}
              >
                <div className="w-12 h-12 rounded-md bg-accent/20 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {problem.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {problem.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Impact Statement */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-accent/10 border-2 border-accent/30 rounded-md p-5 sm:p-8">
            <p className="text-xl sm:text-2xl lg:text-3xl font-black text-white leading-tight">
              <span className="text-accent">{metierOverride?.ville ? `Les ${metierOverride.title.toLowerCase()}s de ${metierOverride.ville.nom}` : "80% des artisans"}</span>{" "}
              {metierOverride?.ville
                ? "méritent un flux constant de clients qualifiés"
                : "manquent de clients et perdent du chiffre d'affaires chaque semaine"}
            </p>
            <p className="text-slate-400 mt-4">
              {metierOverride?.ville
                ? `${metierOverride.ville.contexte}`
                : "Pas assez de demandes, des appels ratés, des devis non relancés. On change ça."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;
